/**
 * PaymentsListController — page /paiements.html (EPIC-042 / 42.4).
 *
 * Hydrate la liste paginée des transactions de paiement depuis
 * `GET /v1/payments` (§7.I, modèle unifié). Filtres flat : `source`
 * (enum PaymentSource), `providerId` (UUID), `from`/`to` (YYYY-MM-DD),
 * `invoiceId` (UUID). Pagination cap 100 (défaut 25).
 *
 * Convention CLAUDE.md respectée : aucun HTML inline en JS, tout passe par
 * `<template>` + `cloneTemplate()` + `fillSlots()` + `setAttribute()` ciblé.
 * Aucune classe CSS inventée — toutes les classes utilisées sont définies
 * dans notamicro.css (copie bit-à-bit de maquettes/shared/styles.css).
 *
 * La page d'édition (42.5) n'est pas livrée — le lien « Modifier » est rendu
 * mais pointe vers `/payments/edition.html?id=…` (cible créée en 42.5). Le
 * filtre « Facture » est rendu mais traité côté UI comme une recherche par
 * numéro/client futur (non-câblé tant que l'endpoint backend n'expose pas
 * la recherche libre — pour l'instant, le filtre est laissé inerte pour
 * éviter de poser des params qui seraient ignorés silencieusement).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ORIGIN_STORAGE_KEY = "nm_filter_origin_payments";

// Libellés UI alignés sur la maquette (§7.I, vocabulaire « moyen de paiement »).
const SOURCE_LABELS = {
  cash: "Espèces",
  bank_transfer: "Virement",
  check: "Chèque",
  wero: "Wero",
  other: "Autre moyen",
};

const STATUS_LABELS = {
  received: "Reçu",
  refunded: "Remboursé",
  disputed: "En litige",
  archived: "Archivé",
};

export default class PaymentsListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "summary",
    "sourceFilter",
    "providerFilter",
    "fromFilter",
    "toFilter",
    "invoiceFilter",
    "originToggleGroup",
    "originToggle",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    originFilter: { type: String, default: "all" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._restoreOriginFromSession();
    this._loadImportCutover();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Filter / paginate actions
  // ---------------------------------------------------------------------------

  filterChanged() {
    this.pageValue = 1;
    this.refresh();
  }

  filterChangedDebounced() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    // L'événement `reset` est dispatché AVANT que le navigateur ne restaure
    // les valeurs par défaut des champs — on réécrit donc explicitement tous
    // les champs lus par `_buildParams()` à vide avant le refresh, sans
    // dépendre du timing du reset natif.
    this.pageValue = 1;
    this.originFilterValue = "all";
    if (this.hasOriginToggleTarget) this.originToggleTarget.checked = true;
    if (this.hasSourceFilterTarget) this.sourceFilterTarget.value = "";
    if (this.hasProviderFilterTarget) this.providerFilterTarget.value = "";
    if (this.hasFromFilterTarget) this.fromFilterTarget.value = "";
    if (this.hasToFilterTarget) this.toFilterTarget.value = "";
    if (this.hasInvoiceFilterTarget) this.invoiceFilterTarget.value = "";
    this._persistOriginToSession();
    this.refresh();
  }

  changeOrigin(event) {
    this.originFilterValue = event.target.checked ? "all" : "native";
    this._persistOriginToSession();
    this.pageValue = 1;
    this.refresh();
  }

  _restoreOriginFromSession() {
    try {
      const stored = window.sessionStorage.getItem(ORIGIN_STORAGE_KEY);
      if (stored === "native" || stored === "all") {
        this.originFilterValue = stored;
        if (this.hasOriginToggleTarget) this.originToggleTarget.checked = stored === "all";
      }
    } catch {
      // sessionStorage indisponible — fallback "all".
    }
  }

  _persistOriginToSession() {
    try {
      window.sessionStorage.setItem(ORIGIN_STORAGE_KEY, this.originFilterValue);
    } catch {
      // sessionStorage indisponible — fail silencieux.
    }
  }

  async _loadImportCutover() {
    if (!this.hasOriginToggleGroupTarget) return;
    try {
      const profile = await api.getOrganizationProfile();
      const cutover = profile?.importCutoverDate ?? null;
      this.originToggleGroupTarget.hidden = !cutover;
    } catch {
      this.originToggleGroupTarget.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");

    const params = this._buildParams();
    try {
      const response = await api.payments.list(params);
      if (seq !== this._fetchSeq) return;
      const items = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? {
        page: 1, perPage: this.perPageValue, total: 0, totalPages: 1,
      };
      this._renderRows(items);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les transactions.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _buildParams() {
    const params = { page: this.pageValue, perPage: this.perPageValue };

    if (this.hasSourceFilterTarget && this.sourceFilterTarget.value) {
      params.source = this.sourceFilterTarget.value;
    }
    if (this.hasProviderFilterTarget && this.providerFilterTarget.value) {
      params.providerId = this.providerFilterTarget.value;
    }
    if (this.hasFromFilterTarget && this.fromFilterTarget.value) {
      params.from = this.fromFilterTarget.value;
    }
    if (this.hasToFilterTarget && this.toFilterTarget.value) {
      params.to = this.toFilterTarget.value;
    }
    if (this.originFilterValue && this.originFilterValue !== "all") {
      params.origin = this.originFilterValue;
    }
    // Le filtre « Facture » est non-câblé tant que le backend n'expose
    // pas de recherche libre — laisser l'input inerte (cf. en-tête).
    return params;
  }

  _renderRows(items) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (items.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-payment-empty"));
      return;
    }

    for (const item of items) {
      const fragment = this.cloneTemplate("tpl-payment-row");
      const isArchived = item.status === "archived";
      const sourceLabel = SOURCE_LABELS[item.paymentSource] ?? item.paymentSource ?? "—";
      const statusLabel = STATUS_LABELS[item.status] ?? item.status ?? "—";

      this.fillSlots(fragment, {
        date: this.formatShortDate(item.transactionDate),
        sourceLabel,
        amount: this._formatAmount(item.amount),
        statusLabel,
      });

      // Facture liée — soit un lien vers la fiche facture, soit un tiret.
      const invoiceCell = fragment.querySelector('[data-slot-element="invoiceLink"]');
      if (invoiceCell) {
        if (item.invoiceId) {
          const link = document.createElement("a");
          link.setAttribute("href", `/facture-detail.html?id=${encodeURIComponent(item.invoiceId)}`);
          link.textContent = item.invoiceNumber ?? "Brouillon";
          invoiceCell.appendChild(link);
        } else {
          invoiceCell.textContent = "—";
          invoiceCell.classList.add("is-muted");
        }
      }

      // Bouton « Modifier » → page d'édition (42.5).
      const editLink = fragment.querySelector('[data-slot-element="editLink"]');
      if (editLink) {
        editLink.setAttribute(
          "href",
          `/paiements-edition.html?id=${encodeURIComponent(item.id)}`,
        );
        editLink.textContent = isArchived ? "Voir" : "Modifier";
      }

      const importedBadge = fragment.querySelector('[data-slot-element="importedBadge"]');
      if (importedBadge && item.origin === "imported") importedBadge.hidden = false;

      // Style ligne archivée (cf. notamicro.css : `.nm-table tbody tr.is-archived td`).
      if (isArchived) {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    this.summaryTarget.textContent = total === 0
      ? "Aucune transaction"
      : `${total} transaction${total > 1 ? "s" : ""}`;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} transaction${total > 1 ? "s" : ""}`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token, text: String(token), label: `Page ${token}`, current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-payment-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _formatAmount(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "—";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }
}
